import { useMutation, useQuery } from "@tanstack/react-query";
import type { FormEvent } from "react";
import { useRef, useState } from "react";

import { ApiError, api } from "../../shared/api/client";
import { image } from "../../shared/assets/manifest";
import { Button } from "../../shared/ui/Button";
import { EmptyState } from "../../shared/ui/EmptyState";
import { LoadingBlock } from "../../shared/ui/LoadingBlock";
import { errorMessage, useToast } from "../../shared/ui/ToastProvider";

type Organization = {
  id: number;
  name: string;
  classCount: number;
  teacherCount: number;
  studentCount: number;
  licenseStatus?: string;
  licenseExpiresAt?: string | null;
  maxClasses?: number | null;
  maxTeachers?: number | null;
  maxStudents?: number | null;
};

const PLAN_LABELS: Record<string, string> = {
  community: "社区版",
  standard: "标准版",
  pro: "专业版",
  enterprise: "企业版"
};

function planLabel(status?: string) {
  return PLAN_LABELS[status || "community"] || status || "社区版";
}

function usageText(used: number, max: number | null | undefined) {
  if (max == null || max <= 0) return `${used} / 不限`;
  return `${used} / ${max}`;
}

function usagePercent(used: number, max: number | null | undefined) {
  if (max == null || max <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round((used / max) * 100)));
}

export function AdminPage() {
  const toast = useToast();
  const formRef = useRef<HTMLFormElement | null>(null);
  const [activationMessage, setActivationMessage] = useState("");
  const [activationError, setActivationError] = useState("");
  const orgs = useQuery({
    queryKey: ["admin", "orgs"],
    queryFn: async () => api<{ items: Organization[] }>("/api/admin/orgs"),
    select: (payload) => payload.data?.items || [],
    retry: false
  });
  const activate = useMutation({
    mutationFn: (payload: { orgId: number; activationCode: string }) =>
      api("/api/admin/activate", { method: "POST", body: JSON.stringify(payload) }),
    onMutate: () => {
      setActivationMessage("");
      setActivationError("");
    },
    onSuccess: () => {
      setActivationMessage("许可证已激活");
      toast("许可证已激活", "success");
      formRef.current?.reset();
      orgs.refetch();
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : "激活失败";
      setActivationError(message);
      toast(message, "error");
    }
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (activate.isPending) return;
    const form = new FormData(event.currentTarget);
    activate.mutate({
      orgId: Number(form.get("orgId")),
      activationCode: String(form.get("activationCode") || "").trim()
    });
  };

  const forbidden = orgs.error instanceof ApiError && orgs.error.status === 403;

  return (
    <main className="admin-page">
      <aside className="admin-nav">
        <div className="brand-lockup">
          <img src={image.star} alt="" />
          <strong>
            班级积分管理系统
            <small>机构管理台</small>
          </strong>
          <img className="brand-rainbow" src={image.flagRainbow} alt="" />
        </div>
        <nav className="admin-side-links">
          <a href="#orgs">机构信息</a>
          <a href="#license">授权管理</a>
          <a href="/">返回主应用</a>
        </nav>
      </aside>
      <section className="admin-content">
        <header className="app-topbar">
          <div>
            <span className="eyebrow">班级积分管理系统</span>
            <h1>机构与授权</h1>
            <p className="setting-desc">查看机构用量，并用激活码升级许可证。</p>
          </div>
          <div className="panel-actions wrap">
            <nav className="admin-mobile-nav" aria-label="管理台导航">
              <a href="#orgs">机构信息</a>
              <a href="#license">授权管理</a>
              <a href="/">返回主应用</a>
            </nav>
            <Button variant="secondary" type="button" disabled={orgs.isFetching} onClick={() => orgs.refetch()}>
              刷新
            </Button>
          </div>
        </header>

        {forbidden ? <EmptyState title="权限不足" detail="仅管理员或机构管理员可进入管理台。" /> : null}
        {orgs.isLoading ? <LoadingBlock /> : null}

        {!forbidden ? (
          <>
            <section id="orgs" className="surface-panel wide">
              <div className="panel-heading">
                <h2>机构信息</h2>
                <span>共 {orgs.data?.length || 0} 个机构</span>
              </div>
              {orgs.data?.length ? (
                <div className="org-grid">
                  {orgs.data.map((org) => (
                    <article className="surface-panel org-card" key={org.id}>
                      <div className="panel-heading">
                        <div>
                          <h2>{org.name}</h2>
                          <small>ORG-{String(org.id).padStart(6, "0")}</small>
                        </div>
                        <span className="small-pill">{planLabel(org.licenseStatus)}</span>
                      </div>
                      <div className="usage-list">
                        <div>
                          <div className="usage-label">
                            <span>班级</span>
                            <strong>{usageText(org.classCount, org.maxClasses)}</strong>
                          </div>
                          <div className="usage-bar">
                            <div style={{ width: `${usagePercent(org.classCount, org.maxClasses)}%` }} />
                          </div>
                        </div>
                        <div>
                          <div className="usage-label">
                            <span>教师</span>
                            <strong>{usageText(org.teacherCount, org.maxTeachers)}</strong>
                          </div>
                          <div className="usage-bar">
                            <div style={{ width: `${usagePercent(org.teacherCount, org.maxTeachers)}%` }} />
                          </div>
                        </div>
                        <div>
                          <div className="usage-label">
                            <span>学生</span>
                            <strong>{usageText(org.studentCount, org.maxStudents)}</strong>
                          </div>
                          <div className="usage-bar">
                            <div style={{ width: `${usagePercent(org.studentCount, org.maxStudents)}%` }} />
                          </div>
                        </div>
                      </div>
                      <p className="setting-desc">
                        有效期：{org.licenseExpiresAt ? org.licenseExpiresAt : "永久有效"}
                      </p>
                    </article>
                  ))}
                </div>
              ) : !orgs.isLoading ? (
                <EmptyState title="暂无机构数据" />
              ) : null}
            </section>

            <section id="license" className="surface-panel">
              <div className="panel-heading">
                <h2>激活许可证</h2>
              </div>
              <form ref={formRef} className="inline-form" onSubmit={onSubmit}>
                <select name="orgId" required defaultValue={orgs.data?.[0]?.id || ""}>
                  {orgs.data?.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
                <input name="activationCode" placeholder="激活码" maxLength={80} required />
                <Button variant="primary" type="submit" disabled={activate.isPending || !orgs.data?.length}>
                  {activate.isPending ? "激活中..." : "激活"}
                </Button>
              </form>
              {activationError ? <div className="inline-error">{activationError}</div> : null}
              {activationMessage ? <div className="inline-success">{activationMessage}</div> : null}
            </section>
          </>
        ) : null}
      </section>
    </main>
  );
}
